import root from 'window-or-global';
import queryString from 'query-string';
import {processParams} from 'epic-common/src/url';
import {getLocale} from 'epic-common/src/locale';

export * from '../../shared/utils/Utils';

const shareImageUrl =
    'https://cdn2.unrealengine.com/Affiliate+Web%2FEG_SAC_Social_Meta-Image-1920x1080-51b0855872208845c06bdcb997f1878028865a12.jpg';

export default function getCurrentLocale(supportedLocales, locale, defaultString) {
    let locales = supportedLocales;
    if (!locales || !Array.isArray(locales)) {
        console.error(
            'Locale options will not render correctly! Expecting an array of seamless immutable objects containing both [name] and [lang] keys!'
        );
        locales = [];
    }

    let currentLocale = defaultString;
    let activeLocale;
    locales.forEach(l => {
        if (l.lang === locale) {
            activeLocale = l;
        }
    });

    if (locale && activeLocale) {
        currentLocale = activeLocale.name;
    }

    locales = locales.sort((localeA, localeB) => {
        const a = localeA && typeof localeA.lang === 'string' ? localeA.lang.toLowerCase() : '';
        const b = localeB && typeof localeB.lang === 'string' ? localeB.lang.toLowerCase() : '';
        if (a > b) return 1;
        if (a < b) return -1;
        return 0;
    });

    return {currentLocale, locales};
}

export function getAssetPath(asset) {
    return `${root.__webpack_asset_path || ''}${asset}`;
}

export function trackProgress() {
    if (
        typeof root.com.epicgames !== 'undefined' &&
        typeof root.com.epicgames.web !== 'undefined' &&
        !!root.com.epicgames.web.track
    ) {
        root.com.epicgames.web.track.loadPixel();
    }
}

export function loadScript(url) {
    if (typeof root.document === 'undefined') {
        console.log('FortniteUtils loadScript skipped, document not available');
        return;
    }
    const scriptTag = root.document.createElement('script');
    scriptTag.setAttribute('type', 'text/javascript');
    scriptTag.setAttribute('src', url);
    root.document.head.appendChild(scriptTag);
}

export function canPlayVideo() {
    if (typeof root.document === 'undefined') {
        console.log('FortniteUtils canPlayVideo: false, document not available');
        return false;
    }
    let canPlay;
    const video = root.document.createElement('video');
    if (video.canPlayType && video.canPlayType('video/webm') !== '') {
        canPlay = 'webm';
    } else if (video.canPlayType && video.canPlayType('video/mp4') !== '') {
        canPlay = 'mp4';
    } else {
        canPlay = 'none';
    }
    return canPlay;
}

export function getMetaTags(messages) {
    const locale = getLocale();

    // Social metadata
    const socialMeta = {
        siteName: messages['epic.affiliate.name'],
        description: messages['epic.affiliate.common.description'],
        twitterDesc: messages['epic.affiliate.common.description'],
        locale: locale ? locale.replace('-', '_') : locale
    };

    let baseUrl;
    if (typeof root.document === 'undefined') {
        baseUrl = root.location.origin;
    } else {
        baseUrl = root.document.getElementsByTagName('html')[0].dataset
            ? root.document.getElementsByTagName('html')[0].dataset.baseurl
            : root.document.getElementsByTagName('html')[0].getAttribute('data-baseurl');
    }

    //if ends with /affiliate add a slash
    if (!baseUrl.match(/.*\/$/)) {
        baseUrl += '/';
    }

    const ogUrl = root.location.href;

    const tags = [
        //Facebook
        {property: 'og:url', content: ogUrl},
        {property: 'og:title', content: socialMeta.siteName},
        {property: 'og:description', content: socialMeta.description},
        {property: 'og:site_name', content: socialMeta.siteName},
        {property: 'og:locale', content: socialMeta.locale},
        {property: 'og:image', content: shareImageUrl},

        //Twitter
        {name: 'twitter:card', content: 'summary'},
        {name: 'twitter:title', content: socialMeta.siteName},
        {name: 'twitter:description', content: socialMeta.twitterDesc},
        {name: 'twitter:image', content: shareImageUrl}
    ];
    return tags;
}

export function getDateParams(input) {
    const date = new Date(input);
    const time = date.getTime();
    const year = date.getFullYear();
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];
    const month = date.getMonth();
    const enMonth = months[month + 1];
    const day = date.getDate() < 9 ? `0${date.getDate()}` : date.getDate();
    const hour = date.getHours();
    const min = date.getMinutes();
    const sec = date.getSeconds();

    return {
        date,
        time,
        year,
        month,
        enMonth,
        day,
        hour,
        min,
        sec
    };
}

export function parseHTML(string) {
    const html = root.document.createElement('div');
    html.innerHTML = string;
    return html;
}

/**
 * If the delta for the x or y coordinates of the mouse have changed from mouse down to mouse up
 *  it is dragging motion and not intended to be a click.
 * @param  {Object} e Event object
 * @return {Boolean}   true/false
 */
export function draggingMotion(e, mouseDown) {
    const allowed = 40;
    const x = e.clientX;
    const y = e.clientY;

    if (mouseDown) {
        if (Math.abs(x - mouseDown.x) > allowed) {
            return true;
        }

        if (Math.abs(y - mouseDown.y) > allowed) {
            return true;
        }
    }

    return false;
}

/**
 * Fisher-Yates shuffle function.  This shuffle is done in-place and is destructive
 * @param array
 */
export function shuffle(array) {
    let unShuffled = array.length;
    let element = null;
    let currentElement;

    // While there remain elements to shuffle…
    while (unShuffled) {
        // Pick a remaining element…
        currentElement = Math.floor(Math.random() * unShuffled--);

        // And swap it with the current element.
        element = array[unShuffled];
        array[unShuffled] = array[currentElement];
        array[currentElement] = element;
    }

    return array;
}

export function convertJsonToFormData(data) {
    const formData = new root.FormData();
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            formData.append(key, data[key]);
        }
    }
    return formData;
}

const getApLocale = () => {
    const locale = getLocale();
    return locale.replace('-', '_');
};

export function navigateToSignin() {
    const returnUrl = root.location.pathname;

    const loginParams = {
        lang: getApLocale(),
        state: returnUrl
    };

    const queryParams = queryString.parse(root.location.search) || {};
    if (queryParams && queryParams.mode) {
        loginParams.mode = queryParams.mode;
    }

    const loginUrl = `${root.appContext || ''}/login?${processParams(loginParams)}`;
    root.location.href = loginUrl;
}

export function navigateToSignout() {
    const returnUrl = root.location.pathname;

    const loginParams = {
        lang: getApLocale(),
        state: returnUrl
    };

    const queryParams = queryString.parse(root.location.search) || {};
    if (queryParams && queryParams.mode) {
        loginParams.mode = queryParams.mode;
    }

    const loginUrl = `${root.appContext || ''}/login?${processParams(loginParams)}`;
    root.location.href = loginUrl;
}

// bind func to class using it
export function onPhoneTextChange(event) {
    const {name, value} = event.target;
    const regex = /([^\d-\\(\\)])+/i;
    this.props.onPhoneNumberChange(name, value.replace(regex, ''));
}

export function resetQueryParams(name) {
    const params = new URLSearchParams(root.location.search);
    params.delete(name);
    const paramsString = params.toString() ? `?${params}` : '';
    const url = decodeURIComponent(`${root.location.pathname}${paramsString}`);
    root.history.replaceState({}, '', url);
}

export const formatFileName = (name: string): string =>
    name
        .split(' ')
        .map(item => item.toLocaleLowerCase())
        .join('_');
